import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { InformationStepCtx } from "web/Pages/Checkout/SubscriptionMUI/InformationStep/withContext";

import __ from "web/Layout/Translations";

import { ErrorData } from "web/api/apiRequestTypes";

import editForm from "web/utils/page/product/subscription/forms/editForm";
import { removeVirtualFields } from "web/utils/page/product/subscription/forms/prepareFormData";
import getErrorInfo from "web/utils/system/essentials/getErrorInfo";

import {
  FormView,
  HandleToggleReceiversModalFn,
  ISubscriptionFormData,
  ISubscriptionFormMode,
  ISubscriptionReceiverActiveForm,
  ISubscriptionReceiverForm,
  ISubscriptionReceiverFormValues,
  SubscriptionFormActions,
  SubscriptionFormReceiver,
} from "web/types/Subscription";

import { useAppContext } from "web/context/app";
import {
  setNotificationError,
  setNotificationSuccess,
} from "web/features/app/appSlice";

import { SubscriptionReceiverMessages as messages } from "../SubscriptionReceiver/subscriptionReceiverMUI";
import SubscriptionReceiverFormDialogEdit from "./ReceiverDialogForms/subscriptionReceiverEditMUI";
import SubscriptionReceiverFormDialogPreview from "./ReceiverDialogForms/subscriptionReceiverPreviewMUI";

interface ISubscriptionReceiverFormProps {
  data: ISubscriptionReceiverForm;
  fieldsRef: React.MutableRefObject<ISubscriptionReceiverFormValues>;
  formData: ISubscriptionFormData;
  mode: ISubscriptionFormMode;
  receiverId: string;
  receiverType: SubscriptionFormReceiver;
  subscriptionId: string;
  viewType: FormView;
  updateFormData: (data: ISubscriptionFormData) => void;
  handleToggleReceiversModal?: HandleToggleReceiversModalFn;
  subscriptionName: string;
  groupName: string;
}

const SubscriptionReceiverFormMUI: FC<ISubscriptionReceiverFormProps> = ({
  data,
  formData,
  mode,
  receiverId,
  receiverType,
  subscriptionId,
  viewType = FormView.CONFIGURATOR,
  updateFormData,
  handleToggleReceiversModal,
  fieldsRef,
  subscriptionName,
  groupName,
}) => {
  const [dialogPreviewOpen, setDialogPreviewOpen] = useState(false);
  const [dialogEditDataOpen, setDialogEditDataOpen] = useState(false);
  const { isMobile } = useAppContext();

  const dispatch = useDispatch();
  const { saveForm, formsError } = useContext(InformationStepCtx) ?? {};
  const {
    activeForm = {},
    awaitsUpdate,
    fields = [],
    type: formType,
    id: formId,
    itemActive,
    receiverEmail,
    submitted,
  } = data ?? {};
  const [isFormDataEmpty, setIsFormDataEmpty] = useState(true);

  useEffect(() => {
    // Sprawdzenie czy formData zawiera dane + dodatkowo sprawdzenie czy fields nie jest pustym obiektem {}
    if (
      formData &&
      formData.fields &&
      Object.keys(formData.fields).length > 0
    ) {
      setIsFormDataEmpty(false);
    } else {
      setIsFormDataEmpty(true);
    }
  }, [formData]);

  const {
    configuration = [],
    description,
    // name,
  } = (activeForm as ISubscriptionReceiverActiveForm) ?? {};
  const { error = false } = formsError?.[formId] ?? {};

  const submitHandler = useCallback(
    (values: ISubscriptionReceiverFormValues) => {
      const writableValues = removeVirtualFields(values);

      // if form is on active subscription - send singly to /edit-receiver-form
      if (submitted || itemActive || awaitsUpdate) {
        editForm({
          subscriptionId,
          receiverId,
          receiverType,
          ...(mode.externalForm
            ? { receiverEmail: writableValues.receiverEmail as string }
            : { fields: writableValues }),
          viewType,
          onSuccess: (result: any) => {
            // On certain views, e.g. My Subscriptions receivers are rendered in modals.
            // Forms are always displayed in modal, so if one was invoked from modal it needs to be restored on save.
            // Result - For get updated data about nextSendAfter, returned after edit-receiver-form
            if (typeof handleToggleReceiversModal === "function") {
              handleToggleReceiversModal({
                id: receiverId,
                ...(mode.externalForm
                  ? {
                      receiverEmail: writableValues.receiverEmail as string,
                      nextSendAfter: result.nextSendAfter,
                    }
                  : { fields: writableValues }),
              });
            }
            handleSubmitSuccess(values);
            setDialogEditDataOpen(false);
          },
          onError: (error: ErrorData) => handleSubmitError(error),
        });
      } else if (typeof saveForm === "function") {
        // if form is on newly chosen subscription - save to context & send on next step
        saveForm({
          formId,
          currentValues: writableValues,
          receiverId,
          subscriptionId,
          clearError: true,
        });
        updateFormData({
          ...formData,
          fields: writableValues,
          filledInByUser: true,
        });
        setDialogEditDataOpen(false);
      }
    },
    [
      submitted,
      itemActive,
      awaitsUpdate,
      subscriptionId,
      receiverId,
      receiverType,
      mode,
      viewType,
      handleToggleReceiversModal,
      saveForm,
      updateFormData,
      formData,
    ]
  );

  const handleSubmitSuccess = useCallback(
    (values: ISubscriptionReceiverFormValues) => {
      const writableValues = removeVirtualFields(values);

      updateFormData({
        fields: {
          ...fieldsRef.current,
          ...writableValues,
        },
        filledInByUser: true,
        edited: true,
        error: false,
      });

      if (typeof saveForm === "function") {
        saveForm({
          formId,
          currentValues: {},
          receiverId,
          updated: true,
          subscriptionId,
          clearError: true,
        });
      }

      dispatch(setNotificationSuccess({ message: messages.formSaveSuccess }));
    },
    []
  );

  const handleSubmitError = useCallback(
    (error: ErrorData) => {
      const additionalInfo = getErrorInfo(error);
      const errorMessage = additionalInfo.violations?.[0]?.message;

      dispatch(
        setNotificationError({
          message:
            mode.actions === SubscriptionFormActions.RESEND
              ? __(messages.editEmailError)
              : __(errorMessage || messages.formSaveError),
        })
      );
    },
    [dispatch]
  );

  const formActions = (() => {
    let actions;

    switch (mode.actions) {
      case SubscriptionFormActions.NONE:
        actions = null;
        break;
      case SubscriptionFormActions.PREVIEW_AND_EDIT:
        actions = (
          <>
            <Button
              variant="text"
              onClick={() => setDialogPreviewOpen(true)}
              disabled={isFormDataEmpty}
              startIcon={<SearchIcon />}
            >
              {__("Zobacz formularz")}
            </Button>
            <Button
              variant="text"
              onClick={() => setDialogEditDataOpen(true)}
              startIcon={<EditIcon />}
            >
              {__("Edytuj")}
            </Button>
          </>
        );
        break;
      case SubscriptionFormActions.PREVIEW:
        actions = (
          <Button
            variant="text"
            onClick={() => setDialogPreviewOpen(true)}
            disabled={isFormDataEmpty}
            startIcon={<SearchIcon />}
          >
            {__("Zobacz formularz")}
          </Button>
        );
        break;
      case SubscriptionFormActions.PREVIEW_OTHERS:
        actions = (
          <Button
            type="button"
            onClick={() => setDialogPreviewOpen(true)}
            disabled={isFormDataEmpty}
            startIcon={<SearchIcon />}
          >
            {__("Zobacz formularz")}
          </Button>
        );
        break;
      case SubscriptionFormActions.EDIT: {
        const EditButton = (
          <Button
            variant="text"
            onClick={() => setDialogEditDataOpen(true)}
            startIcon={<EditIcon />}
            sx={{ mt: 1 }}
            disabled={mode.employeeEditDisabled}
            color={
              mode.awaitsUpdate &&
              !mode.resend &&
              !formData.edited &&
              !formData.filledInByUser &&
              viewType === FormView.CONFIGURATOR
                ? "error"
                : "primary"
            }
          >
            {__("Edytuj dane")}
          </Button>
        );
        actions = mode.employeeEditDisabled ? (
          <Tooltip title={__(messages.employeeEditDisabled)} placement="top">
            {EditButton}
          </Tooltip>
        ) : (
          EditButton
        );
        break;
      }
      case SubscriptionFormActions.RESEND:
        actions = (
          <Button
            variant="text"
            onClick={() => setDialogEditDataOpen(true)}
            startIcon={<EditIcon />}
            sx={{ mt: 1 }}
            color={error || mode.awaitsUpdate ? "error" : "primary"}
          >
            {__("Wyślij ponownie formularz")}
          </Button>
        );
        break;
      default:
        actions = (
          <Button
            variant="outlined"
            size="large"
            onClick={() => setDialogEditDataOpen(true)}
            color={error || mode.awaitsUpdate ? "error" : "primary"}
          >
            {__("Wypełnij formularz")}
            <ChevronRightIcon sx={{ ml: 1.7 }} />
          </Button>
        );
    }

    return error ? (
      <>
        {actions}
        <Typography variant="body2" color="#D32F2F" sx={{ mt: 2 }}>
          {__("Wypełnij dane, aby przejść dalej.")}
        </Typography>
      </>
    ) : (
      actions
    );
  })();

  return (
    <Box>
      {dialogPreviewOpen ? (
        <SubscriptionReceiverFormDialogPreview
          open={dialogPreviewOpen}
          onClose={() => setDialogPreviewOpen(false)}
          isMobile={isMobile}
          groupName={groupName}
          subscriptionName={subscriptionName}
          description={description}
          formData={formData}
          configuration={configuration}
        />
      ) : null}

      {dialogEditDataOpen ? (
        <SubscriptionReceiverFormDialogEdit
          open={dialogEditDataOpen}
          onClose={() => setDialogEditDataOpen(false)}
          isMobile={isMobile}
          groupName={groupName}
          subscriptionName={subscriptionName}
          description={description}
          formData={formData}
          configuration={configuration}
          mode={mode}
          fields={fields}
          formType={formType}
          receiverEmail={receiverEmail}
          nextSendAfter={data.nextSendAfter}
          submitHandler={submitHandler}
          receiverType={receiverType}
          submitted={submitted}
          viewType={viewType}
        />
      ) : null}

      {formActions}
    </Box>
  );
};

export default SubscriptionReceiverFormMUI;
